import type React from 'react';
import { useMemo, useState } from 'react';

import { useGetStream, useGetStreamsLogs } from '@vlabs/api/artemy/client';
import type { StreamLog } from '@vlabs/api/artemy/model';
import { ChevronDownIcon, ChevronUpIcon } from '@vlabs/icons';
import { CopyButton } from '@vlabs/uikit';
import { DateInput } from '@vlabs/uikit/components/controls/date-input/DateInput';
import { Select } from '@vlabs/uikit/components/controls/select/Select';
import { PaginationPanel } from '@vlabs/uikit/components/pagination-v2';
import { dateFormatter } from '@vlabs/uikit/components/table-view/cell/formatters/date';
import type { TOption } from '@vlabs/uikit/types';
// ponytail: осознанный импорт роутов приложения — компонент используется только в point3
import { routes } from '@vlapps/point3/src/app/routes';
import { generatePath } from 'react-router-dom';

import {
  getAnalyticsTypeLabel,
  parseAnalyticsLabel,
  toStreamAnalyticsRecordItem,
} from '@vlabs/monokit/entities/sources';
import { Link } from '@vlabs/monokit/shared/ui/link';
import { selectMenuPortalStyles } from '@vlabs/monokit/shared/ui/select-portal';
import {
  getStreamStatusConfig,
  StreamStatus,
} from '@vlabs/monokit/shared/ui/stream-statuses';

import { useStreamLogsTranslation } from '../i18n';
import type { StreamLogsProps } from '../types';
import st from './StreamLogsContent.module.sass';

const PAGE_SIZE = 25;

const STATUS_VALUES = [0, 1, 2, 3, 4, 5, 6, 7, 8];

const getRowKey = (log: StreamLog): string => `${log.log_time}-${log.analytic_index ?? ''}`;

function StreamLogsContent({ streamId }: StreamLogsProps): React.JSX.Element {
  const { t } = useStreamLogsTranslation();

  const getStream = useGetStream(streamId, { enriched: 0 });
  const stream = getStream.data;

  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState<number | undefined>(undefined);
  const [expandedKey, setExpandedKey] = useState<string | undefined>(undefined);

  const params = useMemo(() => ({
    stream_ids: [streamId],
    page,
    page_size: PAGE_SIZE,
    ...(status !== undefined ? { statuses: [status] } : {}),
    ...(startDate ? { log_time__gte: `${startDate}T00:00:00` } : {}),
    ...(endDate ? { log_time__lt: `${endDate}T23:59:59` } : {}),
  }), [streamId, page, status, startDate, endDate]);

  const getStreamsLogs = useGetStreamsLogs(params);

  const logs = getStreamsLogs.data?.logs ?? [];
  const canNextPage = logs.length >= PAGE_SIZE;

  const analyticsItems = useMemo(
    () => (stream?.analytics ?? []).map(toStreamAnalyticsRecordItem),
    [stream],
  );

  const statusOptions = useMemo<TOption[]>(() => STATUS_VALUES.map((value) => ({
    value: String(value),
    label: getStreamStatusConfig(value).label,
  })), []);

  const getAnalyticTitle = (analyticIndex: number | undefined): string | undefined => {
    if (analyticIndex === undefined) return undefined;
    const item = analyticsItems[analyticIndex];
    if (!item) return undefined;

    const parts = parseAnalyticsLabel(item.label);
    const typeLabel = getAnalyticsTypeLabel(parts?.type ?? item.type ?? '');
    if (!parts) return typeLabel;
    return `${typeLabel} ${parts.number}`;
  };

  const getSelectedStatus = (value: number | undefined): TOption | undefined => {
    if (value === undefined) return undefined;
    return statusOptions.find((option) => Number(option.value) === value);
  };

  const handleStatusChange = (newValue: unknown) => {
    const option = newValue as TOption | null;
    setStatus(option ? Number(option.value) : undefined);
    setPage(1);
  };

  const handleDateChange = (setter: (value: string) => void) => (_dates: Date[], dateStr: string) => {
    setter(dateStr);
    setPage(1);
  };

  const toggleDetails = (key: string) => {
    setExpandedKey((current) => (current === key ? undefined : key));
  };

  return (
    <div className={st.Root}>
      <div className={st.Header}>
        <div>
          <h2 className={st.Title}>{t('modal_title')}</h2>
          {!!stream?.name && <p className={st.Subtitle}>{stream.name}</p>}
        </div>

        <div className={st.Filters}>
          <div className={st.DateRange}>
            <DateInput
              dateFormat="Y-m-d"
              inputProps={{ 'aria-label': t('filters.date_from') }}
              onChange={handleDateChange(setStartDate)}
              placeholder={t('filters.date_from')}
              value={startDate || undefined}
            />
            <span className={st.DateSeparator}>:</span>
            <DateInput
              dateFormat="Y-m-d"
              inputProps={{ 'aria-label': t('filters.date_to') }}
              onChange={handleDateChange(setEndDate)}
              placeholder={t('filters.date_to')}
              value={endDate || undefined}
            />
          </div>
          <Select
            classNamePrefix="StreamLogsStatus"
            isClearable
            menuPortalTarget={document.body}
            onChange={handleStatusChange}
            options={statusOptions}
            placeholder={t('filters.status')}
            styles={selectMenuPortalStyles as unknown as React.ComponentProps<typeof Select>['styles']}
            value={getSelectedStatus(status)}
          />
        </div>
      </div>

      <div className={st.Table}>
        {logs.map((log, index) => {
          // ponytail: в StreamLog нет id — ключ из полей + индекс страницы
          const key = `${getRowKey(log)}-${index}`;
          const isExpanded = expandedKey === key;
          const analyticTitle = getAnalyticTitle(log.analytic_index);

          let details: React.JSX.Element | null = null;
          if (isExpanded) {
            details = (
              <div className={st.Details}>
                <pre className={st.DetailsJson}>
                  {JSON.stringify(log, null, 2)}
                </pre>
                <CopyButton
                  label={t('copy')}
                  size="small"
                  value={log}
                />
              </div>
            );
          }

          let timeCell = '-';
          if (log.log_time) {
            timeCell = dateFormatter(log.log_time);
          }

          let chevron: React.JSX.Element = <ChevronDownIcon />;
          if (isExpanded) {
            chevron = <ChevronUpIcon />;
          }

          return (
            <div
              className={st.LogRow}
              data-testid={`streamLog-${key}`}
              key={key}
            >
              <div className={st.Cell}>
                {timeCell}
              </div>
              <div className={st.Cell}>
                {!!analyticTitle && (
                  <Link
                    surface="default"
                    to={generatePath(routes.sources.streamView, { streamId })}
                    type="secondary"
                  >
                    {analyticTitle}
                  </Link>
                )}
              </div>
              <div className={st.Cell}>
                {log.status !== undefined && (
                  <StreamStatus
                    status={log.status}
                    type="text"
                  />
                )}
              </div>
              <div className={st.Cell}>
                {log.error ?? '-'}
              </div>
              <div className={st.Cell}>
                <button
                  className={st.DetailsButton}
                  data-testid={`streamLog-details-${key}`}
                  onClick={() => toggleDetails(key)}
                  type="button"
                >
                  <span>{t('details')}</span>
                  {chevron}
                </button>
              </div>

              {details}
            </div>
          );
        })}

        {logs.length === 0 && (
          <div className={st.Empty}>
            {t('empty')}
          </div>
        )}
      </div>

      <div className={st.Pagination}>
        <PaginationPanel
          canNextPage={canNextPage}
          canPreviousPage={page > 1}
          gotoPage={(pageIndex) => setPage(pageIndex + 1)}
          nextPage={() => setPage((prev) => prev + 1)}
          // ponytail: бэк не отдаёт total — pageCount оценивается по заполненности страницы
          pageCount={page + (canNextPage ? 1 : 0)}
          pageIndex={page - 1}
          pageSize={PAGE_SIZE}
          previousPage={() => setPage((prev) => prev - 1)}
          setPageSize={() => undefined}
        />
      </div>
    </div>
  );
}

export { StreamLogsContent };
